import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment-timezone';

// Default window mirrors the weekly report / backend: (now -8d startOfDay) ..
// (now -2d endOfDay) anchored to America/Toronto, regardless of the admin's
// own timezone. Stored as millisecond timestamps.
const defaultWindow = () => ({
    from: Number(
        moment()
            .tz('America/Toronto')
            .subtract(8, 'days')
            .startOf('day')
            .format('x'),
    ),
    to: Number(
        moment()
            .tz('America/Toronto')
            .subtract(2, 'days')
            .endOf('day')
            .format('x'),
    ),
});

const initialState = {
    isLoading: false,
    isSaving: false,
    isGeneratingReport: false,
    rows: [],
    total: 0,
    error: null,
    // edits: { [jobId]: unitNumber } — pending, unsaved changes
    edits: {},
    filters: {
        ...defaultWindow(),
        entityId: null,
        entityName: null,
        search: '',
    },
};

const slice = createSlice({
    name: 'weeklyUnitNumbers',
    initialState,
    reducers: {
        fetchList: state => {
            state.isLoading = true;
            state.error = null;
        },
        listFetched: (state, { payload }) => {
            state.rows = payload.rows || [];
            state.total = payload.total || 0;
            state.isLoading = false;
            // drop any edits that no longer match a loaded row
            const ids = new Set(state.rows.map(r => r.jobId));
            Object.keys(state.edits).forEach(id => {
                if (!ids.has(id)) delete state.edits[id];
            });
        },
        listFetchFailed: (state, { payload }) => {
            state.isLoading = false;
            state.error = payload || 'Failed to load unit numbers';
        },
        editUnitNumber: (state, { payload }) => {
            const { jobId, unitNumber } = payload;
            const row = state.rows.find(r => r.jobId === jobId);
            // If the value matches what's already stored, clear the pending edit.
            if (row && (row.unitNumber || '') === unitNumber) {
                delete state.edits[jobId];
            } else {
                state.edits[jobId] = unitNumber;
            }
        },
        discardEdits: state => {
            state.edits = {};
        },
        saveEdits: state => {
            state.isSaving = true;
            state.error = null;
        },
        saveEditsSucceeded: (state, { payload }) => {
            state.isSaving = false;
            // reflect saved values into rows, clear edits
            const saved = payload || {};
            state.rows.forEach(r => {
                if (Object.prototype.hasOwnProperty.call(saved, r.jobId)) {
                    r.unitNumber = saved[r.jobId];
                }
            });
            state.edits = {};
        },
        saveEditsFailed: (state, { payload }) => {
            state.isSaving = false;
            state.error = payload || 'Failed to save unit numbers';
        },
        generateReport: state => {
            state.isGeneratingReport = true;
        },
        generateReportSettled: state => {
            state.isGeneratingReport = false;
        },
        setFilters: (state, { payload }) => {
            state.filters = { ...state.filters, ...payload };
        },
    },
});

export const {
    fetchList,
    listFetched,
    listFetchFailed,
    editUnitNumber,
    discardEdits,
    saveEdits,
    saveEditsSucceeded,
    saveEditsFailed,
    generateReport,
    generateReportSettled,
    setFilters,
} = slice.actions;

export default slice.reducer;

export const selectWeeklyUnitNumbersState = state => state.weeklyUnitNumbers;
